/**
 * Inline calendar date range picker - allows users to select start and end dates
 * by clicking directly in a calendar grid.
 *
 * Replaces separate date input fields with a more visual, intuitive interface.
 * Users can click a start date, navigate to another month if needed, then click an end date.
 *
 * ## Features
 * - Visual month calendar grid
 * - Click first day (sets start), click second day (sets end)
 * - Navigate between months before selecting end date
 * - Hover preview of range
 * - Clear selection button
 * - Month navigation
 * - Keyboard accessible
 *
 * ## UX Patterns
 * - First click on a day: becomes start date (highlighted in blue)
 * - Navigate months if needed (start date is remembered)
 * - Second click: becomes end date (range fills in)
 * - Click selected start: clears selection
 * - Click after range selected: starts new selection
 * - ESC key: clears selection
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/shared/format/date-format';

export interface DateRange {
  readonly startDate: string; // ISO date string 'YYYY-MM-DD'
  readonly endDate: string;   // ISO date string 'YYYY-MM-DD'
}

export interface InlineDateRangePickerProps {
  /** Current selected range */
  readonly value?: DateRange | null;
  /** Callback when range changes */
  readonly onChange: (range: DateRange | null) => void;
  /** Initial month to display (defaults to current month or start of value) */
  readonly initialMonth?: Date;
  /** Minimum selectable date (ISO string) */
  readonly minDate?: string;
  /** Maximum selectable date (ISO string) */
  readonly maxDate?: string;
  /** Additional CSS classes */
  readonly className?: string;
}

interface CalendarDay {
  readonly date: Date;
  readonly dateString: string; // 'YYYY-MM-DD'
  readonly dayOfMonth: number;
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
  readonly isSelectable: boolean;
}

/**
 * The weekday column headers, Monday first (ISO 8601, assumption A5), as
 * catalogue keys rather than literals (R18.9).
 */
const DAY_OF_WEEK_KEYS = [
  'datePicker.weekday.mon',
  'datePicker.weekday.tue',
  'datePicker.weekday.wed',
  'datePicker.weekday.thu',
  'datePicker.weekday.fri',
  'datePicker.weekday.sat',
  'datePicker.weekday.sun',
] as const;

/** Format date as 'YYYY-MM-DD' */
function toISODateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse 'YYYY-MM-DD' to a local `Date`.
 *
 * The three parts are checked rather than trusted: `split('-')` on an input that
 * is not a full ISO date yields fewer than three elements, and passing
 * `undefined` into the `Date` constructor silently produces a date in 1899 (for a
 * missing day) rather than an error — a wrong-but-plausible value, which is worse
 * than an obviously invalid one. An unparseable input therefore returns an
 * Invalid Date, whose comparisons are all false, so a malformed `minDate`/
 * `maxDate` makes days unselectable instead of shifting the calendar.
 */
function parseISODate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    return new Date(Number.NaN);
  }
  return new Date(year, month - 1, day);
}

/** Check if two dates are the same day */
function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/** Check if date is within a range (inclusive) */
function isInRange(date: Date, start: Date, end: Date): boolean {
  return date >= start && date <= end;
}

/** Generate calendar days for a given month */
function generateCalendarDays(
  year: number,
  month: number,
  minDate?: Date,
  maxDate?: Date,
): CalendarDay[] {
  const firstDay = new Date(year, month, 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Start from Monday before the first day of month
  const startDate = new Date(firstDay);
  const dayOfWeek = firstDay.getDay();
  // Adjust: Monday = 1, Sunday = 0 -> need to go back (dayOfWeek === 0 ? 6 : dayOfWeek - 1) days
  const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysBack);

  // Generate 42 days (6 weeks * 7 days) to fill the grid
  const days: CalendarDay[] = [];
  const currentDate = new Date(startDate);

  for (let i = 0; i < 42; i++) {
    const dateString = toISODateString(currentDate);
    const isCurrentMonth = currentDate.getMonth() === month;
    const isToday = isSameDay(currentDate, today);
    
    let isSelectable = isCurrentMonth;
    if (minDate && currentDate < minDate) isSelectable = false;
    if (maxDate && currentDate > maxDate) isSelectable = false;

    days.push({
      date: new Date(currentDate),
      dateString,
      dayOfMonth: currentDate.getDate(),
      isCurrentMonth,
      isToday,
      isSelectable,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

export function InlineDateRangePicker({
  value,
  onChange,
  initialMonth,
  minDate,
  maxDate,
  className,
}: InlineDateRangePickerProps): React.JSX.Element {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    if (initialMonth) return initialMonth;
    if (value?.startDate) return parseISODate(value.startDate);
    return new Date();
  });

  const [hoverDate, setHoverDate] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const minDateObj = minDate ? parseISODate(minDate) : undefined;
  const maxDateObj = maxDate ? parseISODate(maxDate) : undefined;

  const days = generateCalendarDays(year, month, minDateObj, maxDateObj);

  // `en-GB`, matching every other date the application renders (R18.10's
  // day-month-year convention); `en-US` here would have been the one place in the
  // product formatting a date to a different locale than the rest.
  const monthName = currentMonth.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  // Navigation handlers
  const goToPreviousMonth = (): void => {
    setCurrentMonth(new Date(year, month - 1, 1));
  };

  const goToNextMonth = (): void => {
    setCurrentMonth(new Date(year, month + 1, 1));
  };

  const goToToday = (): void => {
    setCurrentMonth(new Date());
  };

  // Selection logic - Click-only mode for better UX
  const handleDayClick = (day: CalendarDay): void => {
    if (!day.isSelectable) return;

    const clickedDate = day.dateString;

    // If no selection, set as start date
    if (!value?.startDate) {
      onChange({ startDate: clickedDate, endDate: clickedDate });
      return;
    }

    // If clicking the same start date, clear selection
    if (value.startDate === clickedDate && value.endDate === clickedDate) {
      onChange(null);
      return;
    }

    // If only start is set (start === end), clicking another day sets the end
    if (value.startDate === value.endDate) {
      const start = parseISODate(value.startDate);
      const clicked = parseISODate(clickedDate);
      
      if (clicked < start) {
        // Clicked before start, swap them
        onChange({ startDate: clickedDate, endDate: value.startDate });
      } else {
        // Clicked after start, normal range
        onChange({ startDate: value.startDate, endDate: clickedDate });
      }
      return;
    }

    // If range is already set, start new selection
    onChange({ startDate: clickedDate, endDate: clickedDate });
  };

  // Hover preview - show what range would look like
  const handleDayMouseEnter = (day: CalendarDay): void => {
    if (!day.isSelectable) return;
    
    // Only show hover preview when we have a start date but no end date yet
    if (value?.startDate && value.startDate === value.endDate) {
      setHoverDate(day.dateString);
    } else {
      setHoverDate(null);
    }
  };

  const handleDayMouseLeave = (): void => {
    setHoverDate(null);
  };

  // Keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      onChange(null);
    }
  };

  // Determine day styles
  const getDayClasses = (day: CalendarDay): string => {
    const start = value?.startDate ? parseISODate(value.startDate) : null;
    const end = value?.endDate ? parseISODate(value.endDate) : null;
    const hover = hoverDate ? parseISODate(hoverDate) : null;
    const dayDate = day.date;

    const isStart = start && isSameDay(dayDate, start);
    const isEnd = end && isSameDay(dayDate, end);
    const isInSelectedRange = start && end && start.getTime() !== end.getTime() && isInRange(dayDate, start, end);
    
    // Show hover preview only when we have start but waiting for end
    const showHoverPreview = start && hover && value?.startDate === value?.endDate;
    const isInHoverRange = showHoverPreview && 
      isInRange(dayDate, Math.min(start.getTime(), hover.getTime()) === start.getTime() ? start : hover,
                        Math.max(start.getTime(), hover.getTime()) === start.getTime() ? start : hover);

    return cn(
      'flex h-9 w-9 items-center justify-center text-[12px] rounded-md transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
      !day.isCurrentMonth && 'text-txt-subtle opacity-40',
      day.isCurrentMonth && 'text-txt',
      day.isSelectable && 'cursor-pointer hover:bg-bg-subtle',
      !day.isSelectable && 'cursor-not-allowed opacity-30',
      day.isToday && 'font-semibold ring-1 ring-primary ring-inset',
      isStart && 'bg-primary text-white font-semibold hover:bg-primary-hover',
      isEnd &&
        !isSameDay(dayDate, start!) &&
        'bg-primary text-white font-semibold hover:bg-primary-hover',
      isInSelectedRange && !isStart && !isEnd && 'bg-primary/20',
      isInHoverRange && !isStart && !isSameDay(dayDate, hover!) && 'bg-primary/10',
    );
  };

  const clearSelection = (): void => {
    onChange(null);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'inline-block rounded-lg border border-border bg-white p-3 shadow-sm',
        className,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="rounded p-1 hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t('datePicker.previousMonth')}
        >
          <ChevronLeft className="h-4 w-4 text-txt" />
        </button>

        <div className="flex flex-1 items-center justify-center gap-2">
          <span className="text-[13px] font-semibold text-txt">{monthName}</span>
          <button
            type="button"
            onClick={goToToday}
            className="text-[11px] text-primary hover:underline"
          >
            {t('datePicker.today')}
          </button>
        </div>

        <button
          type="button"
          onClick={goToNextMonth}
          className="rounded p-1 hover:bg-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={t('datePicker.nextMonth')}
        >
          <ChevronRight className="h-4 w-4 text-txt" />
        </button>
      </div>

      {/* Days of week header */}
      <div className="mb-1 grid grid-cols-7 gap-1">
        {DAY_OF_WEEK_KEYS.map((dayKey) => (
          <div
            key={dayKey}
            className="flex h-9 w-9 items-center justify-center text-[10px] font-medium uppercase text-txt-subtle"
          >
            {t(dayKey)}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1" onMouseLeave={handleDayMouseLeave}>
        {days.map((day, index) => (
          <button
            key={index}
            type="button"
            className={getDayClasses(day)}
            onClick={() => handleDayClick(day)}
            onMouseEnter={() => handleDayMouseEnter(day)}
            disabled={!day.isSelectable}
            aria-label={day.dateString}
            tabIndex={day.isCurrentMonth && day.isSelectable ? 0 : -1}
          >
            {day.dayOfMonth}
          </button>
        ))}
      </div>

      {/* Selection display and clear button */}
      {value && (
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          {/* Through the shared formatter, so the selection reads as
              `dd.MM.yyyy` like every other date in the product (R18.10) rather
              than as the raw `YYYY-MM-DD` wire value. */}
          <div className="text-[11px] text-txt">
            {value.startDate === value.endDate ? (
              <span>{formatDate(value.startDate)}</span>
            ) : (
              <span>
                {formatDate(value.startDate)} → {formatDate(value.endDate)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={clearSelection}
            className="rounded p-1 text-txt-subtle hover:bg-bg-subtle hover:text-txt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={t('datePicker.clearSelection')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
